import { Link, Typography } from "@mui/material";

import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  AnalyticsHeader,
  purchaseSeatLanguages,
  NewYorkYankeesTCListItems,
  NewYorkYankeesCheckoutTerms,
  NewYorkYankeesRegistrationTerms,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";

import LogoImage from "public/images/newyorkyankees/yankees.png";
import Background from "public/images/newyorkyankees/yankees-background.jpg";
import StaticSeatMapImage from "public/images/newyorkyankees/yankeessuite_static_seatmap_image.jpg";
import FindGamesBanner from "public/images/newyorkyankees/yankees-flex-banner.jpg";
import SeatMap3dPreview from "public/images/newyorkyankees/newyorkyankees_flex_3d_preview.png";

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "newyorkyankees_flex",
  browserTitle: "Yankees Flex Pack",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  mlbTeamId: 147,
  teamName: "New York Yankees",

  buckets: {
    enabled: true,
    prioritizeMinGameSelectionCount: true,
    list: [
      {
        id: 2,
        min: 4,
        max: Infinity,
        isPrimary: true,
        name: "Silver Games",
        description: "Select at least 4 Silver games!",
        scrollToTopWhenSelecting: true,
      },
      {
        id: 3,
        min: 1,
        max: 1,
        name: "Gold Games",
        dontCloseWhenHitMin: true,
        description: "Select 1 Gold game!",
        scrollToTopWhenSelecting: true,
        disableScrollToTopWhenSelectingMin: true,
      },
    ],
  },

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-10171-yankees-sec",
    mapId: "blockmap2d",
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [routes.giftCard],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.seatLevel,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 5,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: true,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,
    defaultSeatCount: 2,
    checkoutTimerEnabled: false,
    isPerksEnabled: false,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: false,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    AnalyticsHeader,
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: true,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/nyyreg/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    seatMap3dPreviewImage: SeatMap3dPreview.src,
    faviconImage: LogoImage.src,
    authBackgroundImage: Background.src,
    checkoutStatusPageImage: Background.src,
    findGamesBannerImage: FindGamesBanner.src,
    logoImage: LogoImage.src + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: false,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    gameFiltersSettings: {
      hideSectionFilter: false,
      hideAccessibilityFilter: false,
      disableSeatCounterAfterFirstEventHeld: true,
    },

    shouldDisplayServiceFee: false,
    shouldAddServiceFeeWithPerkPrice: true,

    customAdaLabel: (
      <>
        For accessible seating, please call the ticket office at{" "}
        <Link
          href="tel:6465999999"
          sx={{
            fontWeight: 500,
            color: "primary.main",
            textDecoration: "none",
          }}
        >
          (646) 599-9999
        </Link>
        .
      </>
    ),
  },

  colors: {
    primary: "#132448",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: <NewYorkYankeesTCListItems />,
    checkoutTermsAndCondition: <NewYorkYankeesCheckoutTerms />,
    registrationTermsAndCondition: <NewYorkYankeesRegistrationTerms />,

    purchaseSeats: purchaseSeatLanguages,

    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",
    checkoutSuccessDescription:
      "Thank you for your New York Yankees Flex Pack purchase!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your mlb.com account to continue with seamless access.",
  },
};

export default config;
